import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import {
  INITIAL_CLAIM_CASES,
  type ClaimCase,
  type ClaimReviewStatus,
} from '../data/claims';
import {
  FilterTabs,
  PaginationControls,
  SearchBar,
  SortableHeader,
  type SortDirection,
  usePaginatedRows,
  useTableControls,
  INPUT_CLASS,
} from './TableControls';

type StatusFilter = 'all' | ClaimReviewStatus;
type ClaimSortKey =
  | 'id'
  | 'type'
  | 'facilityName'
  | 'name'
  | 'walletAddress'
  | 'onchainId'
  | 'jurisdiction'
  | 'provider'
  | 'issuer'
  | 'submittedDate'
  | 'riskScore'
  | 'reviewStatus';

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'revoked', label: 'Revoked' },
];

function riskScoreColor(score: number): string {
  if (score < 30) return 'bg-green-100 text-green-700';
  if (score < 70) return 'bg-yellow-100 text-yellow-700';
  return 'bg-red-100 text-red-700';
}

function truncateAddress(addr: string): string {
  if (!addr || addr.length < 12) return addr;
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

function statusClass(status: ClaimReviewStatus): string {
  if (status === 'under_review') return 'bg-yellow-100 text-yellow-700';
  if (status === 'approved') return 'bg-green-100 text-green-700';
  if (status === 'revoked') return 'bg-orange-100 text-orange-700';
  return 'bg-red-100 text-red-700';
}

function toTimestamp(date: string): number {
  const [day, month, year] = date.split('/').map((part) => Number(part));
  return new Date(year, month - 1, day).getTime();
}

type ClaimAction = 'reject' | 'revoke';

const ACTION_COPY: Record<ClaimAction, { title: string; reasonPlaceholder: string; confirmLabel: string }> = {
  reject: {
    title: 'Reject Claim',
    reasonPlaceholder: 'Reason for rejection',
    confirmLabel: 'Reject',
  },
  revoke: {
    title: 'Revoke Claim',
    reasonPlaceholder: 'Reason for revocation (e.g. failed recertification, sanctions update)',
    confirmLabel: 'Revoke',
  },
};

function ClaimActionModal({
  open,
  mode,
  onClose,
  reason,
  onReasonChange,
  onConfirm,
}: {
  open: boolean;
  mode: ClaimAction;
  onClose: () => void;
  reason: string;
  onReasonChange: (value: string) => void;
  onConfirm: () => void;
}) {
  if (!open) return null;
  const copy = ACTION_COPY[mode];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-50 w-full max-w-md rounded-lg border border-border bg-background p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{copy.title}</h2>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="streamline:interface-delete-2-remove-bold-add-button-buttons-delete" className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1.5">Reason</label>
            <textarea
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              placeholder={copy.reasonPlaceholder}
              className={`${INPUT_CLASS} min-h-[90px] resize-y`}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="destructive" size="sm" disabled={!reason.trim()} onClick={onConfirm}>
              {copy.confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ClaimsTable({
  facilityName,
  initialCases = INITIAL_CLAIM_CASES,
}: {
  facilityName?: string;
  initialCases?: ClaimCase[];
}) {
  const [cases, setCases] = useState<ClaimCase[]>(initialCases);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [actionTarget, setActionTarget] = useState<{ id: string; mode: ClaimAction } | null>(null);
  const [actionReason, setActionReason] = useState('');
  const [sortKey, setSortKey] = useState<ClaimSortKey>('submittedDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const { query, setQuery, page, setPage, pageSize, setPageSize, resetPage } = useTableControls();

  const scopedCases = useMemo(
    () => (facilityName ? cases.filter((item) => item.facilityName === facilityName) : cases),
    [cases, facilityName],
  );

  const filteredCases = useMemo(() => {
    let items = scopedCases;

    if (statusFilter !== 'all') {
      items = items.filter((item) => item.reviewStatus === statusFilter);
    }

    if (query.trim()) {
      const q = query.toLowerCase();
      items = items.filter(
        (item) =>
          item.id.toLowerCase().includes(q) ||
          item.type.toLowerCase().includes(q) ||
          item.name.toLowerCase().includes(q) ||
          item.email.toLowerCase().includes(q) ||
          item.walletAddress.toLowerCase().includes(q) ||
          item.onchainId.toLowerCase().includes(q) ||
          item.jurisdiction.toLowerCase().includes(q) ||
          item.provider.toLowerCase().includes(q) ||
          item.issuer.toLowerCase().includes(q) ||
          item.facilityName.toLowerCase().includes(q),
      );
    }

    return items;
  }, [scopedCases, statusFilter, query]);

  const statusCounts = useMemo(
    () => ({
      all: scopedCases.length,
      under_review: scopedCases.filter((item) => item.reviewStatus === 'under_review').length,
      approved: scopedCases.filter((item) => item.reviewStatus === 'approved').length,
      rejected: scopedCases.filter((item) => item.reviewStatus === 'rejected').length,
      revoked: scopedCases.filter((item) => item.reviewStatus === 'revoked').length,
    }),
    [scopedCases],
  );

  const sortedCases = useMemo(() => {
    const statusOrder: Record<ClaimReviewStatus, number> = {
      under_review: 0,
      approved: 1,
      revoked: 2,
      rejected: 3,
    };

    return [...filteredCases].sort((a, b) => {
      let comparison = 0;

      switch (sortKey) {
        case 'id':
          comparison = a.id.localeCompare(b.id);
          break;
        case 'type':
          comparison = a.type.localeCompare(b.type);
          break;
        case 'facilityName':
          comparison = a.facilityName.localeCompare(b.facilityName);
          break;
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'walletAddress':
          comparison = a.walletAddress.localeCompare(b.walletAddress);
          break;
        case 'onchainId':
          comparison = a.onchainId.localeCompare(b.onchainId);
          break;
        case 'jurisdiction':
          comparison = a.jurisdiction.localeCompare(b.jurisdiction);
          break;
        case 'provider':
          comparison = a.provider.localeCompare(b.provider);
          break;
        case 'issuer':
          comparison = a.issuer.localeCompare(b.issuer);
          break;
        case 'submittedDate':
          comparison = toTimestamp(a.submittedDate) - toTimestamp(b.submittedDate);
          break;
        case 'riskScore':
          comparison = a.riskScore - b.riskScore;
          break;
        case 'reviewStatus':
          comparison = statusOrder[a.reviewStatus] - statusOrder[b.reviewStatus];
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredCases, sortDirection, sortKey]);

  const { rows: paginatedCases } = usePaginatedRows(sortedCases, page, pageSize);

  function onSort(nextKey: ClaimSortKey) {
    setSortDirection((prevDirection) =>
      sortKey === nextKey ? (prevDirection === 'asc' ? 'desc' : 'asc') : 'asc',
    );
    setSortKey(nextKey);
    resetPage();
  }

  function handleApprove(caseId: string) {
    setCases((prev) =>
      prev.map((item) =>
        item.id === caseId ? { ...item, reviewStatus: 'approved' as ClaimReviewStatus } : item,
      ),
    );
    toast.success(`Claim ${caseId} approved`);
  }

  function openActionModal(caseId: string, mode: ClaimAction) {
    setActionTarget({ id: caseId, mode });
    setActionReason('');
  }

  function handleConfirmAction() {
    if (!actionTarget || !actionReason.trim()) return;
    const nextStatus: ClaimReviewStatus = actionTarget.mode === 'reject' ? 'rejected' : 'revoked';

    setCases((prev) =>
      prev.map((item) =>
        item.id === actionTarget.id
          ? { ...item, reviewStatus: nextStatus }
          : item,
      ),
    );

    if (actionTarget.mode === 'reject') {
      toast.error(`Claim ${actionTarget.id} rejected`);
    } else {
      toast.error(`Claim ${actionTarget.id} revoked`);
    }
    setActionTarget(null);
    setActionReason('');
  }

  const showFacilityColumn = !facilityName;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <FilterTabs<StatusFilter>
          filters={STATUS_FILTERS}
          active={statusFilter}
          counts={statusCounts}
          onChange={(value) => {
            setStatusFilter(value);
            resetPage();
          }}
        />
        <SearchBar
          value={query}
          onChange={(value) => {
            setQuery(value);
            resetPage();
          }}
          placeholder="Search by type, investor, wallet, onchain ID, jurisdiction, provider, or facility..."
          className="w-full sm:w-[460px]"
        />
      </div>

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50 text-left">
              <SortableHeader<ClaimSortKey>
                label="Case ID"
                sortKey="id"
                activeSortKey={sortKey}
                direction={sortDirection}
                onSort={onSort}
              />
              <SortableHeader<ClaimSortKey>
                label="Type"
                sortKey="type"
                activeSortKey={sortKey}
                direction={sortDirection}
                onSort={onSort}
              />
              {showFacilityColumn && (
                <SortableHeader<ClaimSortKey>
                  label="Facility"
                  sortKey="facilityName"
                  activeSortKey={sortKey}
                  direction={sortDirection}
                  onSort={onSort}
                />
              )}
              <SortableHeader<ClaimSortKey>
                label="Investor"
                sortKey="name"
                activeSortKey={sortKey}
                direction={sortDirection}
                onSort={onSort}
              />
              <SortableHeader<ClaimSortKey>
                label="Onchain ID"
                sortKey="onchainId"
                activeSortKey={sortKey}
                direction={sortDirection}
                onSort={onSort}
              />
              <SortableHeader<ClaimSortKey>
                label="Wallet"
                sortKey="walletAddress"
                activeSortKey={sortKey}
                direction={sortDirection}
                onSort={onSort}
              />
              <SortableHeader<ClaimSortKey>
                label="Jurisdiction"
                sortKey="jurisdiction"
                activeSortKey={sortKey}
                direction={sortDirection}
                onSort={onSort}
              />
              <SortableHeader<ClaimSortKey>
                label="Provider"
                sortKey="provider"
                activeSortKey={sortKey}
                direction={sortDirection}
                onSort={onSort}
              />
              <SortableHeader<ClaimSortKey>
                label="Issuer"
                sortKey="issuer"
                activeSortKey={sortKey}
                direction={sortDirection}
                onSort={onSort}
              />
              <SortableHeader<ClaimSortKey>
                label="Submitted"
                sortKey="submittedDate"
                activeSortKey={sortKey}
                direction={sortDirection}
                onSort={onSort}
              />
              <SortableHeader<ClaimSortKey>
                label="Risk"
                sortKey="riskScore"
                activeSortKey={sortKey}
                direction={sortDirection}
                onSort={onSort}
              />
              <SortableHeader<ClaimSortKey>
                label="Status"
                sortKey="reviewStatus"
                activeSortKey={sortKey}
                direction={sortDirection}
                onSort={onSort}
              />
              <th className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedCases.length === 0 ? (
              <tr>
                <td colSpan={showFacilityColumn ? 13 : 12} className="px-3 py-8 text-center text-sm text-muted-foreground">
                  No claims match your filters.
                </td>
              </tr>
            ) : (
              paginatedCases.map((item) => (
                <tr key={item.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                  <td className="px-3 py-2.5 font-mono text-xs">{item.id}</td>
                  <td className="px-3 py-2.5">
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium">{item.type}</span>
                  </td>
                  {showFacilityColumn && <td className="px-3 py-2.5">{item.facilityName}</td>}
                  <td className="px-3 py-2.5">
                    <div className="font-medium">{item.name}</div>
                    <div className="text-xs text-muted-foreground">{item.email}</div>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs">{truncateAddress(item.onchainId)}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">{truncateAddress(item.walletAddress)}</td>
                  <td className="px-3 py-2.5">{item.jurisdiction}</td>
                  <td className="px-3 py-2.5">{item.provider}</td>
                  <td className="px-3 py-2.5">{item.issuer}</td>
                  <td className="px-3 py-2.5">{item.submittedDate}</td>
                  <td className="px-3 py-2.5">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${riskScoreColor(item.riskScore)}`}>
                      {item.riskScore}/100
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${statusClass(item.reviewStatus)}`}>
                      {item.reviewStatus.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    {item.reviewStatus === 'under_review' ? (
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleApprove(item.id)}>
                          Approve
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => openActionModal(item.id, 'reject')}>
                          Reject
                        </Button>
                      </div>
                    ) : item.reviewStatus === 'approved' ? (
                      <Button variant="destructive" size="sm" onClick={() => openActionModal(item.id, 'revoke')}>
                        Revoke
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PaginationControls
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalItems={sortedCases.length}
        label="claims"
      />

      <ClaimActionModal
        open={actionTarget !== null}
        mode={actionTarget?.mode ?? 'reject'}
        onClose={() => {
          setActionTarget(null);
          setActionReason('');
        }}
        reason={actionReason}
        onReasonChange={setActionReason}
        onConfirm={handleConfirmAction}
      />
    </div>
  );
}
